import {useSelector} from "react-redux";
import {getDsContext} from "@/utils/dsContext";
import {
	useScoutingReports,
	reportError,
	createDefaultTemplates,
} from "@/utils/scoutingReportData";
import ScoutingReportsStatus from "@/components/ScoutingReportsStatus";
import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";

import Page from "@/components/Page";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFModal from "@/components/Modal";
import VLFCard from "@/components/Card";
import VLFSelectNew from "@/components/Input/SelectNew";
import ReportTemplateEditor from "@/components/ReportTemplateEditor";
import {SUCCESSFUL_OPERATION} from "@/utils/constants";
import {
	removeTemplate,
	saveTemplate,
	validateTemplate,
} from "@/utils/scoutingReports";

import "../index.scss";

function RelazioniTemplatesEditor({handleNotification}) {
	const navigate = useNavigate();
	const data = useScoutingReports();
	const {canWrite} = data;
	const templates = data.templates.filter((t) => t.active !== false);
	const [draft, setDraft] = useState(null);
	useEffect(() => {
		if (!draft && templates.length) setDraft(templates[0]);
	}, [draft, templates]);
	const [errors, setErrors] = useState([]);
	const [openConfirm, setOpenConfirm] = useState(false);
	const saved = templates.find((t) => t.id === draft?.id);
	const dirty = JSON.stringify(saved) !== JSON.stringify(draft);

	const open = (template) => {
		setErrors([]);
		setDraft(template);
	};

	const save = async () => {
		const found = validateTemplate(draft);
		setErrors(found);
		if (found.length) {
			handleNotification("Completa i campi evidenziati", "error");
			return;
		}
		try {
			const result = await saveTemplate(draft);
			setDraft(result);
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(reportError(error), "error");
		}
	};

	const remove = async () => {
		try {
			await removeTemplate(draft.id, draft.version);
			setDraft(null);
			setOpenConfirm(false);
		} catch (error) {
			handleNotification(reportError(error), "error");
		}
	};
	const defaults = async () => {
		try {
			await createDefaultTemplates();
		} catch (error) {
			handleNotification(reportError(error), "error");
		}
	};

	const actions = [
		{
			icon: <ArrowBackIcon />,
			title: "Relazioni",
			color: "info",
			onClick: () => navigate("/scouting/relazioni"),
		},
		{
			icon: <AddIcon />,
			label: "Nuovo template",
			title: "Nuovo template",
			disabled: !canWrite,
			onClick: () => open({name: "", fields: []}),
		},
		...(draft
			? [
					{
						icon: <SaveIcon />,
						label: "Salva",
						title: "Salva template",
						disabled: !dirty || !canWrite,
						onClick: save,
					},
				]
			: []),
		...(saved
			? [
					{
						icon: <DeleteIcon />,
						title: "Disattiva template",
						disabled: !canWrite,
						color: "error",
						onClick: () => setOpenConfirm(true),
					},
				]
			: []),
	];

	return (
		<Page className="page-container relazioni-page">
			<Breadcrumbs
				elements={[
					{label: "Relazioni", path: "/scouting/relazioni"},
					{label: "/"},
				]}
				current="Template"
				actions={<MainActions actions={actions} />}
			/>
			<ScoutingReportsStatus data={data} />
			{data.status === "ready" && !data.templates.length && (
				<button type="button" disabled={!canWrite} onClick={defaults}>
					Crea template iniziali
				</button>
			)}
			<div className="relazioni-stack">
				<VLFCard
					title="Template"
					openable={false}
					content={
						<div className="relazione-meta">
							<div>
								<VLFSelectNew
									label="Template da modificare"
									options={templates.map((t) => ({value: t.id, label: t.name}))}
									value={saved ? {value: saved.id, label: saved.name} : null}
									placeholder={draft && !saved ? "Nuovo template" : undefined}
									onChange={(option) =>
										option && open(templates.find((t) => t.id === option.value))
									}
									sorted={false}
									isClearable={false}
									noMargin
								/>
							</div>
						</div>
					}
				/>
				{draft && (
					<VLFCard
						title={saved ? "Voci di " + saved.name : "Nuovo template"}
						openable={false}
						content={
							<>
								<fieldset
									disabled={!canWrite}
									style={{border: 0, padding: 0, margin: 0}}
								>
									<ReportTemplateEditor
										template={draft}
										onChange={setDraft}
										errors={errors}
									/>
								</fieldset>
								<p className="relazione-hint">
									Le modifiche valgono per le relazioni nuove.
								</p>
							</>
						}
					/>
				)}
			</div>
			<VLFModal
				open={openConfirm}
				title="Disattivazione"
				content={
					<div className="mt-2">
						Disattivare il template "{draft?.name}"? Le relazioni già scritte
						restano come sono.
					</div>
				}
				confirm={remove}
				close={() => setOpenConfirm(false)}
				confirmLabel="Disattiva"
			/>
		</Page>
	);
}

// Una bozza non può attraversare cambi di utente, club o stagione.
export default function RelazioniTemplatesPage(props) {
	useSelector((state) => state.user);
	useSelector((state) => state.token);
	useSelector((state) => state.season?.id);
	return <RelazioniTemplatesEditor key={getDsContext()} {...props} />;
}
